import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unifiedApiService } from '../../services/unified';
import { queryKeys } from '../../lib/queryClient';
import type {
  Resume,
  ResumeUploadResponse,
  DetailedResumeStatus,
  RetryResumeProcessingRequest,
  ResumeWithCandidate,
  AdminResumeFilters,
  BulkResumeStatusUpdate,
  BulkOperationResult,
} from '../../services/unified/types';

/**
 * Query hook for getting user resumes
 */
export const useUserResumes = () => {
  return useQuery({
    queryKey: queryKeys.resumes.list(),
    queryFn: (): Promise<Resume[]> => {
      return unifiedApiService.resume.getUserResumes();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Query hook for getting a single resume
 */
export const useResume = (id: string) => {
  return useQuery({
    queryKey: queryKeys.resumes.detail(id),
    queryFn: (): Promise<Resume> => {
      return unifiedApiService.resume.getResume(id);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Query hook for getting resume processing status with auto-polling
 */
export const useResumeProcessingStatus = (id: string) => {
  return useQuery({
    queryKey: queryKeys.resumes.status(id),
    queryFn: (): Promise<DetailedResumeStatus> => {
      return unifiedApiService.resume.getProcessingStatus(id);
    },
    enabled: !!id,
    refetchInterval: (query) => {
      // Poll every 3 seconds if still processing
      const data = query.state.data;
      if (data?.status === 'processing' || data?.status === 'submitted') {
        return 3000;
      }
      return false;
    },
    staleTime: 0, // Always refetch for processing status
  });
};

/**
 * Query hook for getting admin resumes with filters
 */
export const useAdminResumes = (filters?: AdminResumeFilters) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'resumes', filters],
    queryFn: (): Promise<ResumeWithCandidate[]> => {
      return unifiedApiService.resume.getAdminResumes(filters);
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Mutation hook for uploading resume
 */
export const useUploadResume = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      file,
      userId,
      label,
      onProgress,
    }: {
      file: File;
      userId?: string;
      label?: string;
      onProgress?: (progress: number) => void;
    }): Promise<ResumeUploadResponse> => {
      return unifiedApiService.resume.uploadResume(file, userId, label, onProgress);
    },
    onSuccess: () => {
      // Invalidate resumes list to show new upload
      queryClient.invalidateQueries({ queryKey: queryKeys.resumes.all });
      // Invalidate profile to update resume status
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.profile });
    },
    onError: (error) => {
      console.error('Resume upload failed:', error);
    },
  });
};

/**
 * Mutation hook for deleting resume
 */
export const useDeleteResume = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string): Promise<void> => {
      return unifiedApiService.resume.deleteResume(id);
    },
    onSuccess: () => {
      // Invalidate user resumes cache to reflect deletion
      queryClient.invalidateQueries({ queryKey: queryKeys.resumes.all });
      // Invalidate profile to update resume status
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.profile });
    },
    onError: (error) => {
      console.error('Resume deletion failed:', error);
    },
  });
};

/**
 * Mutation hook for updating resume label
 */
export const useUpdateResumeLabel = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, label }: { id: string; label: string }): Promise<Resume> => {
      return unifiedApiService.resume.updateResumeLabel(id, label);
    },
    onSuccess: () => {
      // Invalidate user resumes cache to reflect label update
      queryClient.invalidateQueries({ queryKey: queryKeys.resumes.all });
    },
    onError: (error) => {
      console.error('Resume label update failed:', error);
    },
  });
};

/**
 * Mutation hook for scanning resume
 */
export const useScanResume = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      return unifiedApiService.resume.scanResume(id);
    },
    onSuccess: (_, id) => {
      // Invalidate processing status to start polling
      queryClient.invalidateQueries({ queryKey: queryKeys.resumes.status(id) });
      // Invalidate resume details
      queryClient.invalidateQueries({ queryKey: queryKeys.resumes.detail(id) });
    },
  });
};

/**
 * Mutation hook for retrying resume processing
 */
export const useRetryProcessing = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: RetryResumeProcessingRequest }): Promise<void> => {
      return unifiedApiService.resume.retryProcessing(id, request);
    },
    onSuccess: (_, { id }) => {
      // Invalidate processing status to start polling again
      queryClient.invalidateQueries({ queryKey: queryKeys.resumes.status(id) });
      // Invalidate resume details
      queryClient.invalidateQueries({ queryKey: queryKeys.resumes.detail(id) });
    },
  });
};

/**
 * Mutation hook for downloading resume
 */
export const useDownloadResume = () => {
  return useMutation({
    mutationFn: async ({ id, filename }: { id: string; filename: string }): Promise<Blob> => {
      const blob = await unifiedApiService.resume.downloadResume(id);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return blob;
    },
    onError: (error) => {
      console.error('Resume download failed:', error);
    },
  });
};

/**
 * Mutation hook for bulk updating resume status (admin)
 */
export const useBulkUpdateResumeStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (request: BulkResumeStatusUpdate): Promise<BulkOperationResult> => {
      return unifiedApiService.resume.bulkUpdateResumeStatus(request);
    },
    onSuccess: () => {
      // Invalidate all resumes to reflect bulk changes
      queryClient.invalidateQueries({ queryKey: queryKeys.resumes.all });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.admin.all, 'resumes'] });
    },
  });
};

/**
 * Custom hook for resume upload with progress tracking
 */
export const useResumeUploadWithProgress = () => {
  const uploadMutation = useUploadResume();
  
  const uploadWithProgress = (
    file: File,
    userId: string,
    onProgress?: (progress: number) => void
  ) => {
    return uploadMutation.mutateAsync({ file, userId, onProgress });
  };
  
  return {
    ...uploadMutation,
    uploadWithProgress,
  };
};
